import { useEffect, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";

import * as echarts from "echarts";
import { Currency } from "shared/utils/Currency";
import ComboFilters from "pages/Reports/ComboFilters";
import { getAllMonthsUntilNow } from "pages/Reports/Reports";
import { ReportsAPI } from "providers/ReportsAPI";
import dayjs from "dayjs";
import { Loading } from "@components";

export default function LifeCycleInvoicing() {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);

  const totalData = [];

  const months = getAllMonthsUntilNow(false);

  const [selectedMonth, setSelectedMonth] = useState(months[0]);

  const daysInMonth = Array.from(
    {
      length: dayjs(
        `${selectedMonth.year}-${String(selectedMonth.id).padStart(2, "0")}`
      )
        .endOf("month")
        .date(),
    },
    (_, i) => i + 1
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      const chartDOM = document.getElementById("invoicing-chart");
      const chart = echarts.init(chartDOM, null, {
        renderer: "svg",
      });

      const colors = ["#e1ffb4", "#6AF3EE", "#a67666"];

      const labels = ["Novos", "Onboarding", "Engajados"];

      if (!rawData?.length) return;

      const today = dayjs();

      let reversedData = [...rawData];

      reversedData = [...rawData].reverse();

      const seriesMap = {};
      labels.forEach((label, i) => {
        seriesMap[label] = reversedData.map((d, did) => {
          const value = d[label.toLowerCase()]?.value ?? 0;
          return totalData[did] <= 0 ? 0 : value;
        });
      });

      const options = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "none",
          },
          backgroundColor: "#2B2B2B",
          borderRadius: 8,
          padding: [8, 8],
          borderWidth: 0,
          textStyle: {
            color: "#FFF",
            fontSize: 10,
          },
          formatter: (params) => {
            const dataIndex = params[0].dataIndex;
            const d = reversedData[dataIndex];
            const date = reversedData[dataIndex]?.date || "Sem data";

            const getBlock = (label, key, bgColor, labelColor) => {
              const value = d?.[key]?.value ?? 0;
              const growth = d?.[key]?.growth ?? 0;

              const isNegative = growth < 0;
              const color = isNegative ? "#e48186" : "#51D9A3";

              const growthHTML =
                growth === 0
                  ? ""
                  : `<span style="color:${color};font-weight:800;margin-left:4px;">
                      ${growth > 0 ? "+" : ""}${(growth || 0).toFixed(2)}% ${
                      growth > 0 ? "↑" : "↓"
                    }
                    </span>`;

              return `
                <div style="background-color: ${bgColor};padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:${labelColor};font-weight:700">
                    ${label}:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Currency.formatCurrency(value)} 
                  </span>  
                  ${growthHTML}
                </div>
              `;
            };

            const formatGrowth = (growth) => {
              if (growth === 0) return "";

              const isPositive = growth > 0;
              const color = isPositive ? "#51D9A3" : "#e48186";
              const sign = isPositive ? "+" : "";
              const arrow = isPositive ? "↑" : "↓";

              return `
                <span style="color:${color};font-weight:800;margin-left:4px;">
                  ${sign}${(growth || 0).toFixed(2)}% ${arrow}
                </span>
              `;
            };

            return `
              <div>
                <div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <div style="color:#fff;opacity:70%;line-height:16px;font-weight:600;margin-bottom:2px;">
                    ${date}
                  </div>

                  <span style="color:#8a8af5;font-weight:700">
                    Faturamento total:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Currency.formatCurrency(d.total.value)} 
                  </span>
                  ${formatGrowth(d.total.growth)}
                </div>

                ${getBlock(
                  "Novos",
                  "novos",
                  "rgb(225 255 180 / 10%)",
                  "#e1ffb4"
                )}
                ${getBlock(
                  "Onboarding",
                  "onboarding",
                  "rgba(106 243 238 / 10%)",
                  "#6AF3EE"
                )}
                ${getBlock(
                  "Engajados",
                  "engajados",
                  "rgba(166 118 102 / 10%)",
                  "#a67666"
                )}
              </div>
            `;
          },
        },
        grid: {
          left: 0,
          right: 10,
          top: 30,
          bottom: 0,
          containLabel: true,
        },
        xAxis: [
          {
            type: "category",
            data: Array.from(daysInMonth),
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            axisLine: {
              lineStyle: {
                color: "rgba(217, 217, 217, 0.5)",
              },
            },
            splitLine: {
              lineStyle: {
                color: "rgba(51, 51, 51, 0.55)",
                width: 1,
                type: "solid",
              },
            },
            axisLabel: {
              formatter: (value) => Currency.formatCurrency(value),
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        series: labels.map((name, sid) => {
          const data = (reversedData || []).map((d, did) => {
            const value = d[name.toLowerCase()]?.value ?? 0;
            return totalData[did] <= 0 ? 0 : value;
          });

          return {
            name,
            type: "bar",
            stack: "total",
            barWidth: "80%",
            data,
            label: {
              show: name === "Engajados",
              position: "top",
              lineHeight: 20,
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: 10,
              fontWeight: "semibold",
              formatter: (params) => {
                const index = params.dataIndex;
                const total = labels.reduce((sum, label) => {
                  const val =
                    seriesMap[label]?.[index] ??
                    reversedData[index][label.toLowerCase()]?.value ??
                    0;
                  return sum + val;
                }, 0);
                if (total <= 0) return "";
                return Currency.formatCurrency(total);
              },
            },
            itemStyle: {
              color: colors[sid],
            },
          };
        }),
      };

      options && chart.setOption(options);

      const ro = new ResizeObserver(() => {
        chart.resize();
      });
      ro.observe(chartDOM);

      return () => {
        ro.unobserve(chartDOM);
        ro.disconnect();
        chart.dispose();
      };
    }
  }, [rawData]);

  useEffect(() => {
    setRawData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const provider = new ReportsAPI();
      const response = await provider.lifeCycle(
        "total",
        selectedMonth.year,
        selectedMonth.month
      );
      setRawData(response);
      setLoading(false);
    };

    fetchData();
  }, [selectedMonth]);

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between mb-20">
        <div className="d-flex align-center relative gap-5">
          <p className={styles.label}>Faturamento dos estágios no período</p>
        </div>
        <div className="d-flex align-center">
          <ComboFilters
            options={months}
            onSelect={(option) => setSelectedMonth(option)}
            selectedOption={selectedMonth}
          />
        </div>
      </div>

      <div className={styles.chart}>
        <div
          id="invoicing-chart"
          style={{
            width: "100%",
            height: 350,
            display: loading ? "none" : "block",
          }}
        />
        {loading ? (
          <div
            className="d-flex align-center justify-center"
            style={{ height: 350 }}
          >
            <Loading />
          </div>
        ) : null}
      </div>
    </div>
  );
}
